<template>
  <aside class="agent-control-panel">
    <!-- Header -->
    <div class="panel-header">
      <h2 class="panel-title">
        <AtlasLogo :size="18" variant="brand" class="panel-title-mark" />
        RoleMapper Atlas
      </h2>
      <v-btn icon small aria-label="Close panel" @click="$emit('close')">
        <v-icon size="20">mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- New chat -->
    <div class="panel-section">
      <v-btn color="primary" block depressed @click="$emit('new-chat')">
        <v-icon size="18" left>mdi-plus</v-icon>
        New chat
      </v-btn>
    </div>

    <!-- Search -->
    <div class="panel-section">
      <v-text-field
        v-model="search"
        placeholder="Search conversations"
        prepend-inner-icon="mdi-magnify"
        dense
        outlined
        hide-details
        clearable
      />
    </div>

    <!-- Conversation list -->
    <div class="conversation-scroller">
      <div
        v-if="filteredConversations.length === 0 && conversations.length > 0"
        class="empty-hint"
      >
        No conversations match "{{ search }}".
      </div>
      <div v-else-if="conversations.length === 0" class="empty-hint">
        Your past conversations will appear here.
      </div>
      <div v-else>
        <div
          v-for="conv in filteredConversations"
          :key="conv.conversation_id"
          class="conversation-row"
          :class="{ 'conversation-row--active': conv.conversation_id === activeConversationId }"
          @click="$emit('switch', conv.conversation_id)"
        >
          <div class="conversation-main">
            <div class="conversation-title">{{ conv.title }}</div>
            <div v-if="conv.last_message_preview" class="conversation-preview">
              {{ conv.last_message_preview }}
            </div>
            <div class="conversation-meta">
              {{ conv.message_count }} messages · {{ formatDate(conv.updated_date) }}
            </div>
          </div>
          <v-btn
            icon
            x-small
            class="conversation-delete"
            title="Delete"
            @click.stop="$emit('delete', conv.conversation_id)"
          >
            <v-icon size="16">mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

  </aside>
</template>

<script>
import { computed, defineComponent, ref } from 'vue';
import AtlasLogo from './AtlasLogo.vue';

export default defineComponent({
  name: 'AgentControlPanel',
  components: { AtlasLogo },
  props: {
    conversations: { type: Array, default: () => [] },
    activeConversationId: { type: String, default: '' },
  },
  emits: ['close', 'new-chat', 'switch', 'delete'],
  setup(props) {
    const search = ref('');

    /**
     * Client-side filter. Cheap for expected list sizes (cap 50/user per
     * backend). Matches title + preview case-insensitively.
     */
    const filteredConversations = computed(() => {
      const q = search.value.trim().toLowerCase();
      if (!q) return props.conversations;
      return props.conversations.filter((c) => {
        return (
          c.title.toLowerCase().includes(q) ||
          (c.last_message_preview || '').toLowerCase().includes(q)
        );
      });
    });

    /**
     * Relative date display — feels natural when scanning recent work.
     * Exact dates feel cold.
     */
    function formatDate(iso) {
      if (!iso) return '';
      const normalised = iso.indexOf('T') >= 0 ? iso : iso.replace(' ', 'T');
      const date = new Date(normalised);
      const ms = Date.now() - date.getTime();
      const minutes = Math.floor(ms / 60000);
      if (minutes < 1) return 'just now';
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      return date.toLocaleDateString();
    }

    return {
      search,
      filteredConversations,
      formatDate,
    };
  },
});
</script>

<style scoped>
/* Width set by the parent overlay container — the component fills
   whatever space it's given so it can float in the left-gutter
   whitespace rather than being a fixed 320px drawer. */
.agent-control-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: rgb(249 250 251);
  overflow: hidden;
}

/* Header */
.panel-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid rgb(229 231 235);
}
.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: rgb(17 24 39);
  letter-spacing: -0.01em;
}
.panel-title-mark {
  flex-shrink: 0;
}

/* Sections */
.panel-section {
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid rgb(229 231 235);
}

/* Conversation list */
.conversation-scroller {
  flex: 1;
  overflow-y: auto;
}
.empty-hint {
  padding: 24px 16px;
  font-size: 12px;
  color: rgb(107 114 128);
  text-align: center;
}
.conversation-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.12s ease;
  border-bottom: 1px solid rgb(243 244 246);
}
.conversation-row:hover {
  background: rgb(243 244 246);
}
.conversation-row--active {
  background: rgb(238 242 255);
}
.conversation-main {
  flex: 1;
  min-width: 0;
}
.conversation-title {
  font-size: 14px;
  font-weight: 500;
  color: rgb(17 24 39);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-preview {
  font-size: 12px;
  color: rgb(107 114 128);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}
.conversation-meta {
  font-size: 11px;
  color: rgb(156 163 175);
  margin-top: 2px;
}
.conversation-delete {
  opacity: 0;
  flex-shrink: 0;
  margin: 0 !important;
}
.conversation-row:hover .conversation-delete {
  opacity: 1;
}
</style>
